import { Close } from "@mui/icons-material";
import { Box, Breakpoint, Button, Dialog, DialogActions, DialogContent, DialogProps, DialogTitle, IconButton } from "@mui/material";
import { MutableRefObject, ReactNode, useEffect, useRef, useState } from "react";
import Draggable, { PositionOffsetControlPosition } from "react-draggable";
import { getResource } from "./localization";
import { createAxios } from "./axios";
import { UseListStore } from "./listStore";

export function useDialog(renderContent: (close: () => void, dialogProps: DialogProps) => ReactNode, maxWidth: Breakpoint | null = "md",
    config?: {
        dialogProps?: Partial<DialogProps>;
        draggable?: boolean;
        dialogPool?: MutableRefObject<DialogPool>;
        offsetDirectionMultiplierX?: number;
        offsetDirectionMultiplierY?: number;
        onClose?(): void;
        usingCustomDialogTag?: boolean;
    }
) {
    const [positionOffset, setPositionOffset] = useState<PositionOffsetControlPosition>({ x: 0, y: 0 });
    const [visible, setVisible] = useState(false);
    const [onClose, setOnClose] = useState<(() => void) | null>(null);
    const nodeRef = useRef(null);
    const [isCloseDisabled, setIsCloseDisabled] = useState(false);
    function close(isExternal: boolean) {
        if (isCloseDisabled && isExternal) {
            return;
        }
        setVisible(false);
        onClose?.();
        config?.onClose?.();
        if (config?.dialogPool) {
            config.dialogPool.current.openDialogs--;
        }
    }
    function renderDialog(): ReactNode {
        const dialogProps: DialogProps = { ref: nodeRef, open: true, onClose: () => close(true), maxWidth: maxWidth ?? undefined, fullWidth: true, ...config?.dialogProps };
        return config?.usingCustomDialogTag ? renderContent(() => close(false), dialogProps) : <Dialog {...dialogProps}>
            <Box m={2}>
                {renderContent(() => close(false), dialogProps)}
            </Box>
        </Dialog>;
    }
    return {
        render(): ReactNode {
            return !visible ? null : !config?.draggable ? renderDialog() :
                <Draggable positionOffset={positionOffset} handle=".MuiDialogTitle-root" nodeRef={nodeRef}>{renderDialog()}</Draggable>;
        },
        show(onClose?: () => void, disableClose?: boolean) {
            if (visible) {
                return;
            }
            setOnClose(() => onClose || null);
            setVisible(true);
            setPositionOffset({ x: 0, y: 0 });
            setIsCloseDisabled(disableClose || false);
            if (config?.dialogPool) {
                const offset = config.dialogPool.current.openDialogs * 30;
                setPositionOffset({ x: offset * (config.offsetDirectionMultiplierX ?? 1), y: offset * (config.offsetDirectionMultiplierY ?? 1) });
                config.dialogPool.current.openDialogs++;
            }
        }
    };
}

export function renderClose(close: () => void) {
    return <IconButton
        aria-label="close"
        onClick={close}
        sx={{
            position: 'absolute',
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
        }}>
        <Close />
    </IconButton>
}

export function useSimpleDialog(): SimpleDialog {
    const [message, setMessage] = useState<ReactNode>(null);
    const [customButtons, setCustomButtons] = useState<((close: () => void) => JSX.Element) | null>(null);
    const [title, setTitle] = useState<string | null>(null);
    const dialog = useDialog(close => <>
        <DialogTitle>{title}</DialogTitle>
        <DialogContent style={{ whiteSpace: "pre-line" }}>{message}</DialogContent>
        <DialogActions>
            {customButtons != null ? customButtons(close) : <Button onClick={close}>Ok</Button>}
        </DialogActions>
    </>, null);
    return {
        render: dialog.render,
        show(title: string, message: ReactNode, onClose?: () => void, customButtons?: (close: () => void) => JSX.Element, disableClose?: boolean) {
            setTitle(title);
            setMessage(message);
            setCustomButtons(() => customButtons || null);
            dialog.show(onClose, disableClose);
        }
    }
}

export interface SimpleDialog {
    render(): ReactNode;
    show(title: string, message: ReactNode, onClose?: () => void, customButtons?: (close: () => void) => JSX.Element, disableClose?: boolean): void;
}

export function yesNoButtons(yes: () => void, no?: () => void) {
    return (close: () => void): JSX.Element => <>
        <OnKey keyName="Enter" callback={() => { yes(); close(); }} />
        <Button onClick={() => { yes(); close(); }} color="positive">{getResource("Ui.Yes", "Yes")}</Button>
        <Button onClick={() => { no && no(); close(); }} color="negative">{getResource("Ui.No", "No")}</Button>
    </>;
}

export function retryButton(retry: () => void) {
    return (close: () => void): JSX.Element => <>
        <OnKey keyName="Enter" callback={() => { retry(); close(); }} />
        <Button onClick={() => { retry(); close(); }}>{getResource("Ui.Retry", "Retry")}</Button>
    </>;
}

export function OnKey(props: { keyName: string; callback(): void; }) {
    useEffect(() => {
        const handler = (p: KeyboardEvent) => {
            if (p.key === props.keyName) {
                props.callback();
            }
        };
        document.addEventListener("keypress", handler);
        return () => {
            document.removeEventListener("keypress", handler);
        };
    });
    return null;
}

export interface DialogPool {
    openDialogs: number;
}

export function useListItemDeleteApi<T>(useStore: UseListStore<T>, getPath: (item: T) => string, itemName = "item", dialog = useSimpleDialog()) {
    async function showDialog() {
        dialog.show("Confirm deletion", `Delete selected ${itemName}?`, undefined, yesNoButtons(async () => {
            try {
                await createAxios().delete(getPath(useStore.getState().selectedItem!));
            }
            finally {
                useStore.getState().fetch({ unintrusive: true });
            }
        }));
    }

    return { dialog, showDialog };
}
